import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import './styles/index.scss';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { ThemeProvider } from '@mui/material/styles';
import { getDarkTheme, getTheme } from './styles/theme';
import { Provider } from 'react-redux';
import { store } from './store';
import config from '../package.json';
import { QueryClientProvider, QueryClient } from 'react-query';
import * as Sentry from '@sentry/react';
import {
  captureConsoleIntegration,
  extraErrorDataIntegration,
} from '@sentry/integrations';
import {
  Routes,
  BrowserRouter,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import { Theme } from '@emotion/react';
import { convertHexToRGBNumbers } from './pages/AIBotComponents/AIBotHelper/helper';

const version = config?.version ?? '0.1.0';

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN || '',
  integrations: [
    new Sentry.BrowserTracing({
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [
        // "localhost",
        'https://assessment.hirehunch.com',
        // 'https://betaassessment.hirehunch.com',
      ],
    }),
    captureConsoleIntegration({
      levels: ['log', 'error'],
    }),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    extraErrorDataIntegration({
      depth: 7,
    }),
    new Sentry.Replay({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],

  environment: `${process.env.REACT_APP_ENV ?? 'local'}`,
  release: `HH-Assessment-${version}`,
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Session Replay
  enabled: process.env.REACT_APP_ENV === 'production',
  replaysSessionSampleRate: 1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  normalizeDepth: 10,
});

export const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const client = new QueryClient();

const RenderedApp = () => {
  const [userTheme, setUserTheme] = useState<Theme>(getTheme());
  const [themeVars, setThemeVars] = useState({
    primaryColor: '#4738F5',
    secondaryColor: '#FF5C2E',
    primaryHoverColor: '#4738F51F',
    themeMode: 'light-theme',
  });

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--ui-primary',
      themeVars.primaryColor
    );
    document.documentElement.style.setProperty(
      '--ui-secondary',
      themeVars.secondaryColor
    );
    document.documentElement.style.setProperty(
      '--ui-primary-bg',
      `rgba(${convertHexToRGBNumbers(themeVars.primaryColor)}, 0.12)`
    );
    if (themeVars.themeMode === 'light-theme') {
      setUserTheme(getTheme());
    } else {
      setUserTheme(getDarkTheme());
    }
  }, [themeVars]);

  React.useEffect(() => {
    const handleThemeUpdate = (event: any) => {
      if (event.detail === 'dark-theme') {
        setUserTheme(getDarkTheme());
        setThemeVars((prev) => ({
          ...prev,
          themeMode: 'dark-theme',
        }));
        return;
      } else if (event.detail === 'light-theme') {
        setUserTheme(getTheme());
        setThemeVars((prev) => ({
          ...prev,
          themeMode: 'light-theme',
        }));
        return;
      }

      Object.entries(event.detail).forEach((item) => {
        const [key, value] = item;
        setThemeVars((prev) => ({
          ...prev,
          [key]: value,
        }));
      });
    };
    document.addEventListener('updateTheme', handleThemeUpdate);

    return () => {
      document.removeEventListener('updateTheme', handleThemeUpdate);
    };
  }, []);

  return (
    <ThemeProvider theme={userTheme}>
      <Provider store={store}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </Provider>
    </ThemeProvider>
  );
};

ReactDOM.render(
  <React.StrictMode>
    <QueryClientProvider client={client}>
      <RenderedApp />
    </QueryClientProvider>
  </React.StrictMode>,
  document.getElementById('root')
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
